import ComposeBody from './ComposeBody';
import ComposeBottom from './ComposeBottom';
import ComposeTop from './ComposeTop';
import { observer } from 'mobx-react';
import { IStickyDockingPlugin } from 'owa-editor-docking-plugin';
import { lazyUpdateRibbonWidth } from 'owa-editor-ribbonplugin';
import { isFeatureEnabled } from 'owa-feature-flags';
import { keydownHandler } from 'owa-hotkeys';
import { addInitialAction, InitialActions } from 'owa-initial-actions';
import { lazyCreateMailComposeAdapter, runAfterNotificationSetup } from 'owa-mail-addins';
import { lazyHasInlineImage } from 'owa-mail-addins';
import shouldAddinsPopulate from 'owa-mail-addins/lib/utils/shouldAddinsPopulate';
import { onComposeAddinsInitialized } from 'owa-mail-compose-actions';
import clearAttachmentFilesToUpload from 'owa-mail-compose-actions/lib/actions/clearAttachmentFilesToUpload';
import closeCompose from 'owa-mail-compose-actions/lib/actions/closeCompose';
import trySaveMessage, { isSaving } from 'owa-mail-compose-actions/lib/actions/trySaveMessage';
import { ComposeViewState, ComposeControl } from 'owa-mail-compose-store';
import addAttachments from 'owa-mail-compose-actions/lib/utils/addAttachments';
import { isPublicFolderComposeViewState } from 'owa-mail-compose-actions/lib/utils/createPublicFolderComposeViewState';
import handleOnSend from 'owa-mail-compose-actions/lib/utils/handleOnSend';
import {
    isGroupScenario,
    isViewStateDirty,
} from 'owa-mail-compose-actions/lib/utils/viewStateUtils';
import { resetFocus } from 'owa-mail-focus-manager';
import { getCommands } from 'owa-mail-hotkeys';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import { DarkModeContext } from 'owa-theme';
import * as React from 'react';
import {
    default as discardCompose,
    shouldShowDiscardConfirmDialog,
} from 'owa-mail-compose-actions/lib/actions/discardCompose';
import {
    registerComposeFocusManager,
    unregisterComposeFocusManager,
} from '../utils/composeFocusManager';
import ComposePlugins, {
    findComposePlugins,
} from 'owa-mail-compose-actions/lib/plugins/ComposePlugins';
import getAutoFocusControl from 'owa-mail-compose-actions/lib/utils/getAutoFocusControl';
import {
    lazyInitializeAddinsForItem,
    lazyDeinitializeAddinsForItem,
    lazyCloseTaskPaneAddinCommand,
    MessageComposeAdapter,
    getComposeHostItemIndex,
} from 'owa-addins-core';

const styles = require('./Compose.scss');
const classNames = require('classnames');

export interface ComposeProps {
    viewState: ComposeViewState;
    initialActions: InitialActions;
    className?: string;
    senderPersona?: JSX.Element;
    onUnmountFocusCallback?: () => void;
}

const AUTO_SAVE_INTERVAL = 30000;
const AUTO_SAVE_INTERNAL_GROUPS = 300000; // 5 minutes
const FOCUS_TIMER = 10;

const enum ResizeEventState {
    NotSubscribed,
    Subscribed,
    Unmounted,
}

/**
 * Scope for artificial resize events.
 * Empty string is the default scope.
 *
 * When a FlexPane mounts or unmounts, the
 * artificial resize it fires is on this scope.
 **/
const RESIZE_EVENT_SCOPE: string = '';

@observer
export default class Compose extends React.Component<ComposeProps, {}> {
    private top: ComposeTop;
    private body: ComposeBody;
    private bottom: ComposeBottom;
    private autoSaveDraftTimer: number;
    private rootDiv: HTMLDivElement;
    private setFocusTimer: number;
    private resizeTimer: number;
    private plugins: ComposePlugins;
    private resizeEventState = ResizeEventState.NotSubscribed;

    constructor(props: ComposeProps) {
        super(props);
        const { viewState } = this.props;
        this.plugins = findComposePlugins(viewState);
    }

    componentDidMount() {
        this.autoSaveDraftTimer = window.setInterval(
            this.autoSaveDraft,
            isGroupScenario(this.props.viewState) ? AUTO_SAVE_INTERNAL_GROUPS : AUTO_SAVE_INTERVAL
        );
        registerComposeFocusManager(this);
        lazySubscribeToResizeEvent.import().then(subscribeToResizeEvent => {
            if (this.resizeEventState == ResizeEventState.NotSubscribed) {
                subscribeToResizeEvent(
                    this.onResize,
                    RESIZE_EVENT_SCOPE,
                    this.rootDiv.ownerDocument.defaultView
                );
                this.resizeEventState = ResizeEventState.Subscribed;
            }
        });
        addInitialAction(this.props.initialActions, () => {
            this.onResize();
        });
        this.focus();
        if (shouldAddinsPopulate(this.props.viewState)) {
            lazyCloseTaskPaneAddinCommand.import().then(closeTaskPaneAddinCommand => {
                closeTaskPaneAddinCommand();
            });

            lazyCreateMailComposeAdapter.import().then(createMailComposeAdapter => {
                const adapter: MessageComposeAdapter = createMailComposeAdapter(
                    this.props.viewState
                );
                lazyInitializeAddinsForItem
                    .import()
                    .then(initializeAddinsForItem => {
                        initializeAddinsForItem(
                            getComposeHostItemIndex(this.props.viewState.composeId),
                            adapter
                        );
                    })
                    .then(
                        // trigger onComposeAddinsInitialized event once MessageComposeAdapter is created and addins are initialized.
                        () => {
                            onComposeAddinsInitialized(
                                getComposeHostItemIndex(this.props.viewState.composeId)
                            );
                        }
                    );
            });
        }

        runAfterNotificationSetup(() => {
            const viewState = this.props.viewState;
            addAttachments(viewState, viewState.attachmentFilesToUpload).then(() => {
                lazyHasInlineImage
                    .import()
                    .then(hasInlineImage => {
                        if (
                            this.plugins.addinPlugin &&
                            hasInlineImage(viewState.attachmentFilesToUpload)
                        ) {
                            // TODO: VSO 26855: addinPluggin is not guaranteed to have been initialized
                            this.plugins.addinPlugin.triggerContentChangedByAddinEvent();
                        }
                    })
                    .then(() => {
                        clearAttachmentFilesToUpload(viewState);
                    });
            });
        });
    }

    componentWillUnmount() {
        this.cancelResizeTimer();
        unregisterComposeFocusManager(this);
        window.clearInterval(this.autoSaveDraftTimer);
        if (this.resizeEventState == ResizeEventState.Subscribed) {
            lazyUnsubscribeFromResizeEvent.importAndExecute(this.onResize, RESIZE_EVENT_SCOPE);
        }
        this.resizeEventState = ResizeEventState.Unmounted;

        if (this.props.onUnmountFocusCallback) {
            this.props.onUnmountFocusCallback();
        } else {
            resetFocus();
        }

        isFeatureEnabled('rp-addins') &&
            lazyDeinitializeAddinsForItem.import().then(deinitializeAddinsForItem => {
                deinitializeAddinsForItem(getComposeHostItemIndex(this.props.viewState.composeId));
            });
    }

    render() {
        const { className, viewState } = this.props;
        return (
            <DarkModeContext.Provider value={viewState.isDarkMode}>
                <div
                    className={classNames(className, styles.composeOuterContainer)}
                    ref={this.rootDivRefCallback}>
                    <div
                        className={
                            !viewState.isDarkMode ? styles.composeInnerContainer : undefined
                        }>
                        <ComposeTop
                            {...this.props}
                            plugins={this.plugins}
                            ref={ref => (this.top = ref)}
                        />
                        <ComposeBody
                            {...this.props}
                            plugins={this.plugins}
                            ref={ref => (this.body = ref)}
                        />
                        {/*
                            Workaround VSO 8885: Compose: JS error when set indent then do another formatting in Edge
                            Add an empty DIV under composeBodyPart to avoid composeBottomPart got broken
                            TODO: (VSO 8885) Remove this workaround once we have an official fix
                        */}
                        <div />
                        <ComposeBottom
                            ref={ref => (this.bottom = ref)}
                            isHidden={viewState.docking.isBottomDocking}
                            {...this.props}
                            plugins={this.plugins}
                            onSend={this.sendMessage}
                            onDiscard={this.discardDraft}
                        />
                        <div className={styles.bottomBorder} ref={this.setStickyBottomBorder} />
                    </div>
                    {this.props.children}
                </div>
            </DarkModeContext.Provider>
        );
    }

    /**
     * Callback when the compose pane gains focus from focus manager
     */
    public focus() {
        if (!this.setFocusTimer) {
            // Always do focus in async mode because this function can be called from an action,
            // and at that time the component may not be updated by React yet.
            // Use setTimeout rather than RAF to allow browser finish a reflow first and user can
            // see the view, the we do focus to avoid a force reflow.
            this.setFocusTimer = window.setTimeout(() => {
                if (this.body && this.top) {
                    const controlToFocus = getAutoFocusControl(this.props.viewState);

                    if (controlToFocus == ComposeControl.Editor) {
                        this.body.trySetFocus();
                    } else {
                        this.top.trySetFocus(controlToFocus);
                    }
                }
                this.setFocusTimer = 0;
            }, FOCUS_TIMER);
        }
    }

    public getRootDiv() {
        return this.rootDiv;
    }

    private setStickyBottomBorder = (ref: HTMLDivElement) => {
        const dockingPlugin = this.plugins ? (this.plugins.dockingPlugin as any) : null;
        if (this.props.viewState.docking.useStickyDocking && dockingPlugin) {
            const stickyDockingPlugin = dockingPlugin as IStickyDockingPlugin;
            stickyDockingPlugin.setStickyBottomBorder(ref);
        }
    };

    private rootDivRefCallback = (div: HTMLDivElement) => {
        if (this.plugins.dockingPlugin) {
            this.plugins.dockingPlugin.setInitialVisiblePart(div);
        }

        this.rootDiv = div;
    };

    private autoSaveDraft = () => {
        const { viewState } = this.props;
        this.plugins.contentOperationPlugin.updateContentToViewState();

        // Don't auto save if item creation is in progress
        if (!isSaving(viewState) && isViewStateDirty(viewState)) {
            trySaveMessage(viewState, true /*isAutoSave*/);
        }
    };

    private cancelResizeTimer() {
        if (this.resizeTimer) {
            window.clearTimeout(this.resizeTimer);
            this.resizeTimer = 0;
        }
    }

    private onResize = () => {
        this.cancelResizeTimer();
        this.resizeTimer = window.setTimeout(() => {
            this.resizeTimer = 0;
            if (this.rootDiv) {
                const clientWidth = this.rootDiv.clientWidth;
                lazyUpdateRibbonWidth.importAndExecute(this.props.viewState.ribbon, clientWidth);
            }
            if (this.bottom) {
                this.bottom.resize();
            }
        }, 1);
    };

    @keydownHandler(getCommands().saveDraft)
    saveDraftKeyboardShortcut() {
        this.plugins.contentOperationPlugin.updateContentToViewState();
        trySaveMessage(this.props.viewState);
    }

    @keydownHandler(getCommands().sendMail)
    sendMailKeyboardShortcut() {
        this.sendMessage();
    }

    private sendMessage = () => {
        this.plugins.contentOperationPlugin.updateContentToViewState();
        if (isPublicFolderComposeViewState(this.props.viewState)) {
            trySaveMessage(this.props.viewState);
            closeCompose(this.props.viewState);
        } else {
            handleOnSend(this.props.viewState).then(error => {
                if (!error) {
                    return;
                }

                // On send failure because of invalid recipients,
                // if there is an unresolved recipient,
                // set focus to the recipient well so the user
                // can fix the recipient.
                if (error.message === 'InvalidRecipients') {
                    [
                        {
                            recipientWell: this.props.viewState.toRecipientWell,
                            controlToFocus: ComposeControl.RecipientWellTo,
                        },
                        {
                            recipientWell: this.props.viewState.ccRecipientWell,
                            controlToFocus: ComposeControl.RecipientWellCc,
                        },
                        {
                            recipientWell: this.props.viewState.bccRecipientWell,
                            controlToFocus: ComposeControl.RecipientWellBcc,
                        },
                    ].some(({ recipientWell, controlToFocus }) => {
                        // If there was a querystring set and it failed to be resolved,
                        // then it will still be populated as non-empty on the
                        // recipientWell.
                        //
                        // Focus to it so the user is prompted to resolve this recipient.
                        if (
                            recipientWell &&
                            recipientWell.queryString &&
                            recipientWell.queryString.length > 0
                        ) {
                            this.top.trySetFocus(controlToFocus);
                            return true;
                        }
                        return false;
                    });
                }
            });
        }
    };

    @keydownHandler(
        getCommands().discardDraft,
        true /*stopPropagation*/,
        true /*preventDefault*/,
        true /*allowHotkeyOnTextFields*/
    )
    discardDraftKeyboardShortcut() {
        this.discardDraft();
    }

    private discardDraft = () => {
        if (
            this.plugins &&
            this.plugins.ribbonPlugin &&
            shouldShowDiscardConfirmDialog(this.props.viewState)
        ) {
            this.plugins.ribbonPlugin.workaroundFocusIssueForFirefox();
        }

        this.plugins.contentOperationPlugin.updateContentToViewState();
        discardCompose(this.props.viewState);
    };
}
